import React, { useEffect, useRef, useState } from 'react';
import { IModalWindow, ModalWindow } from '../../components/modal/ModalWindow';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { useOrdersContext } from '../../store/order/order-context';
import { fetchActivityLogList, setActivityLogPagination } from '../../store/order/actions/activity-log';
import { TablesNames } from '../../store/tables/tables-context';
import { Table } from '../../components/table/Table';
import clsx from 'clsx';
import { ActivityLogFilters } from '../../components/activityLog/ActivityLogFilters';
import { getParsedJsonData } from '../../utils/utilities';

import './ActivityLogPage.scss';

export const ActivityLogPage = (): JSX.Element => {
	const isMounted = useRef(false);

	const { orderContextState, dispatch } = useOrdersContext();
	const { activityLogList, isActivityLogListLoading, activityLogTablePageSize, activityLogTablePageNumber, activityLogTotalItemCount } = orderContextState;
	const { merchantState: { chosenMerchant }} = useMerchants();

	const [tenantId, setTenantId] = useState<string>('');
	const [correlationId, setCorrelationId] = useState<string>('');
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	useEffect(() => {
		if (!isMounted.current) return;
		void fetchActivityLogList(dispatch, activityLogTablePageNumber + 1, activityLogTablePageSize, chosenMerchant, tenantId, correlationId);
	}, [activityLogTablePageNumber, activityLogTablePageSize, chosenMerchant]);

	useEffect(() => {
		if (!isMounted.current) {
			// first mount
			isMounted.current = true;
		}
	}, []);

	const openModal = (data): void => {
		setModalState({
			isOpen: true,
			modalTitle: 'Activity information',
			contentClassName: data ? 'd-block pb-1' : 'd-flex',
			modalText: !data && 'There is no Activity information',
			content: data && (
				<div className="sense-script m-6">
					<pre className="code-area p-5">
						{data}
					</pre>
				</div>
			)
		});
	};

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const rowActions = (rowData): JSX.Element => {
		const actions = [
			{
				key: 'data',
				label: 'Data',
				information: rowData?.original?.data
			},
			{
				key: 'meta',
				label: 'Meta Data',
				information: rowData?.original?.meta
			}
		];

		const getActionTitle = (action): JSX.Element => {
			const { key, label, information } = action;
			const data = getParsedJsonData(information);

			return (
				<span key={key}>
					<span
						className={clsx(
							'text-black fs-7',
							key === 'data' && 'me-3',
							information ? 'fw-bolder cursor-pointer' : 'text-muted  cursor-not-allowed',
							label === 'Meta Data' && 'ms-1 ps-4 action-log-border'
						)}
						onClick={(): void => information && openModal(data)}
					>
						{label}
					</span>
				</span>
			);
		};

		return (
			<div className="mx-auto w-120px">
				{actions?.map(action => getActionTitle(action))}
			</div>
		);
	};

	return (
		<>
			<ActivityLogFilters
				tenantId={tenantId}
				setTenantId={setTenantId}
				correlationId={correlationId}
				setCorrelationId={setCorrelationId}
			/>

			<Table
				name={TablesNames.activityLogTableData}
				data={activityLogList ?? []}
				pageNumber={activityLogTablePageNumber}
				pageSize={activityLogTablePageSize}
				isLoading={isActivityLogListLoading}
				totalItemCount={activityLogTotalItemCount}
				setPagination={(value): void => setActivityLogPagination(dispatch, value)}
				enableRowActions={true}
				rowActions={rowActions}
				actionColumnsSize={100}
			/>
			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>
		</>
	);
};
